import { useState } from "react";
import { formatNum, formatPct } from "../lib/constants";
import SectionCard from "../components/SectionCard";
import KpiCard from "../components/KpiCard";
import HeatmapChart from "../components/charts/HeatmapChart";
import LeyendaEstado from "../components/pipeline/LeyendaEstado";
import { PIPELINE_STAGES, PIPELINE_TRANSICIONES, getEstadoPredominante } from "../mocks/pipelineData";
import { STAGE_KPIS, DATOS_POR_PERIODO, NOSHOW_POR_PERIODO, CANCELACIONES_POR_PERIODO } from "../mocks/data";
import { useFilters } from "../context/FiltersContext";
import {
  Hospital, Stethoscope, FlaskConical, Pill, DollarSign, RefreshCw,
  Users, TrendingDown, TrendingUp, ArrowRight, Clock, Circle, MapPin,
} from "lucide-react";

const iconMap = { Hospital, Stethoscope, FlaskConical, Pill, DollarSign, RefreshCw, Circle };

const PIPELINE_TO_STAGE_KEY = {
  acceso: "busqueda",
  atencion: "consulta",
  diagnostica: "laboratorio",
  tratamiento: "cirugia",
  financiero: "pago",
  seguimiento: "seguimiento",
};

export default function Ambitos() {
  const [selected, setSelected] = useState(PIPELINE_STAGES[2]);
  const { filters } = useFilters();

  // Datos reales del período
  const periodoData = DATOS_POR_PERIODO[filters.periodo] || DATOS_POR_PERIODO["Abril 2026"];
  const noshowData = NOSHOW_POR_PERIODO[filters.periodo] || NOSHOW_POR_PERIODO["Abril 2026"];
  const cancelData = CANCELACIONES_POR_PERIODO[filters.periodo] || CANCELACIONES_POR_PERIODO["Abril 2026"];

  // Ratio de escalado basado en datos reales del período
  const ratio = periodoData.dg.episodios / 123718;

  // Escalar volumen de etapa
  const escalarVolumen = (vol) => vol != null ? Math.round(vol * ratio) : null;

  // Centros reales (basados en canales.dbo.reservas2 — distribución real)
  const centrosBase = [
    { nombre: "Santa Lucía", pct: 23.7, fuga_pct: noshowData.por_centro?.find((c) => c.centro === "Santa Lucía")?.pct || 15.9 },
    { nombre: "Clínica San Carlos", pct: 22.3, fuga_pct: noshowData.por_centro?.find((c) => c.centro === "Clínica San Carlos")?.pct || 14.6 },
    { nombre: "San Joaquín", pct: 15.5, fuga_pct: noshowData.por_centro?.find((c) => c.centro === "San Joaquín")?.pct || 21.5 },
    { nombre: "Escuela Militar", pct: 10.9, fuga_pct: noshowData.por_centro?.find((c) => c.centro === "Escuela Militar")?.pct || 14.5 },
    { nombre: "San Jorge", pct: 5.3, fuga_pct: noshowData.por_centro?.find((c) => c.centro === "San Jorge")?.pct || 18.7 },
    { nombre: "Lira", pct: 5.6, fuga_pct: 15.5 },
  ];
  const totalPacientes = periodoData.dg.pacientes;
  const centros = centrosBase.map((c) => ({
    ...c,
    pacientes: Math.round(totalPacientes * c.pct / 100),
  }));

  // Previsión real (basada en adm_convenios distribución)
  const previsionData = [
    { p: "FONASA", pct: 45, color: "#3B82F6" },
    { p: "ISAPRE Colmena", pct: 22, color: "#7C3AED" },
    { p: "ISAPRE Banmédica", pct: 15, color: "#EC4899" },
    { p: "ISAPRE Cruz Blanca", pct: 10, color: "#F59E0B" },
    { p: "Particular", pct: 5, color: "#F97316" },
    { p: "Otros", pct: 3, color: "#64748B" },
  ].map((x) => ({
    ...x,
    pacientes: Math.round(totalPacientes * x.pct / 100),
  }));

  // Heatmap
  const heatmapLabels = PIPELINE_STAGES.map((s) => s.nombre.length > 12 ? s.nombre.slice(0, 12) + "…" : s.nombre);
  const heatmapSeries = PIPELINE_STAGES.map((from, i) => ({
    name: heatmapLabels[i],
    data: PIPELINE_STAGES.map((to, j) => {
      if (i === j) return { x: heatmapLabels[j], y: 100 };
      if (j === i + 1) {
        const trans = PIPELINE_TRANSICIONES[i];
        return { x: heatmapLabels[j], y: trans?.pct_perdida != null ? Math.round(100 - trans.pct_perdida) : 0 };
      }
      if (j > i) {
        let conv = 100;
        for (let k = i; k < j; k++) {
          const t = PIPELINE_TRANSICIONES[k];
          if (t?.pct_perdida != null) conv *= (100 - t.pct_perdida) / 100;
        }
        return { x: heatmapLabels[j], y: Math.round(conv) };
      }
      return { x: heatmapLabels[j], y: 0 };
    }),
  }));

  // KPIs de etapa seleccionada
  const volumen = escalarVolumen(selected.volumen);
  const maxQuiebre = selected.quiebres.filter((q) => q.porcentaje != null).sort((a, b) => b.porcentaje - a.porcentaje)[0];
  const estado = getEstadoPredominante(selected.sub_etapas);
  const Icon = iconMap[selected.icono] || Circle;

  return (
    <div className="space-y-6" data-testid="ambitos-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Análisis por Etapa</h1>
        <p className="text-sm text-slate-500 mt-1">
          Drill-down de cada macro-etapa del viaje con sub-etapas, quiebres y acciones · {filters.periodo}
        </p>
      </div>

      {/* KPIs resumen del período */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Pacientes DG</div>
          <div className="mt-1 text-xl font-bold text-slate-900 tabular-nums">{formatNum(periodoData.dg.pacientes)}</div>
          <LeyendaEstado estado="REAL" size="dot" />
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Consultas</div>
          <div className="mt-1 text-xl font-bold text-slate-900 tabular-nums">{formatNum(periodoData.dg.episodios)}</div>
          <LeyendaEstado estado="REAL" size="dot" />
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Cancelaciones</div>
          <div className="mt-1 text-xl font-bold text-rose-600 tabular-nums">{formatNum(cancelData.canceladas)}</div>
          <span className="text-xs text-slate-500 tabular-nums">{cancelData.pct_canceladas}%</span>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">No-Show</div>
          <div className="mt-1 text-xl font-bold text-amber-600 tabular-nums">{formatNum(noshowData.no_show)}</div>
          <span className="text-xs text-slate-500 tabular-nums">{noshowData.pct_no_show}%</span>
        </div>
      </div>

      {/* 6 Macro-Etapas grid */}
      <SectionCard title="Las 6 etapas del viaje" subtitle={`Selecciona una etapa para ver el detalle · ${filters.periodo}`} testid="stages-grid">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {PIPELINE_STAGES.map((s) => {
            const StageIcon = iconMap[s.icono] || Circle;
            const isSelected = selected.key === s.key;
            return (
              <button
                key={s.key}
                data-testid={`stage-${s.key}`}
                onClick={() => setSelected(s)}
                className={`rounded-2xl p-4 text-center transition-all border ${
                  isSelected
                    ? "border-transparent shadow-lg -translate-y-0.5 text-white"
                    : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:-translate-y-0.5"
                }`}
                style={isSelected ? { backgroundColor: s.color } : {}}
              >
                <div className="flex justify-center mb-2">
                  <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${isSelected ? "bg-white/20" : ""}`}
                       style={!isSelected ? { backgroundColor: `${s.color}15` } : {}}>
                    <StageIcon className="w-5 h-5" style={!isSelected ? { color: s.color } : { color: "white" }} />
                  </div>
                </div>
                <div className={`text-[10px] font-bold uppercase tracking-wider ${isSelected ? "text-white/70" : "text-slate-400"}`}>
                  Etapa {s.id}
                </div>
                <div className="text-sm font-semibold mt-1">{s.nombre}</div>
                <div className={`text-lg font-bold mt-1 tabular-nums ${isSelected ? "text-white" : "text-slate-900"}`}>
                  {escalarVolumen(s.volumen) != null ? formatNum(escalarVolumen(s.volumen)) : "—"}
                </div>
              </button>
            );
          })}
        </div>
      </SectionCard>

      {/* KPIs de la etapa seleccionada */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard testid="kpi-amb-volumen" label="Volumen" value={volumen != null ? formatNum(volumen) : "—"} suffix="pacientes" icon={Users} accent="violet" />
        <KpiCard testid="kpi-amb-conversion" label="Conversión desde anterior" value={selected.conversion_anterior != null ? selected.conversion_anterior.toFixed(1) : "—"} suffix="%" icon={TrendingUp} accent="emerald" />
        <KpiCard testid="kpi-amb-fuga" label="Mayor quiebre" value={maxQuiebre?.porcentaje != null ? maxQuiebre.porcentaje.toFixed(1) : "—"} suffix="%" icon={TrendingDown} accent="rose" />
        <KpiCard testid="kpi-amb-tiempo" label="Tiempo promedio" value={(() => { const sk = PIPELINE_TO_STAGE_KEY[selected.key]; const kpi = sk ? STAGE_KPIS[sk] : null; return kpi?.tiempo != null ? kpi.tiempo : "—"; })()} suffix={(() => { const sk = PIPELINE_TO_STAGE_KEY[selected.key]; const kpi = sk ? STAGE_KPIS[sk] : null; return kpi?.tiempo != null ? "min" : ""; })()} icon={Clock} accent="amber" />
      </div>

      {/* Header de etapa seleccionada */}
      <div className="flex items-center gap-3 rounded-2xl p-4 border"
           style={{ backgroundColor: `${selected.color}10`, borderColor: `${selected.color}40` }}>
        <div className="h-12 w-12 rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-md"
             style={{ backgroundColor: selected.color }}>
          <Icon className="w-6 h-6" />
        </div>
        <div className="flex-1">
          <h2 className="text-xl font-bold text-slate-900">Etapa {selected.id}: {selected.nombre}</h2>
          <p className="text-sm text-slate-600">{selected.sub_etapas.length} sub-etapas · {selected.quiebres.length} quiebres · {filters.periodo}</p>
        </div>
        <LeyendaEstado estado={estado} />
      </div>

      {/* Sub-etapas tabla */}
      <SectionCard title={`Sub-etapas de ${selected.nombre}`} subtitle={`Detalle de cada paso · ${filters.periodo}`} testid="sub-etapas-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200">
                <th className="py-3 px-4">Sub-etapa</th>
                <th className="py-3 px-4 text-right">Pacientes</th>
                {selected.sub_etapas.some((s) => s.conversion != null) && <th className="py-3 px-4 text-right">Conversión</th>}
                <th className="py-3 px-4">Fuente</th>
                <th className="py-3 px-4 text-center">Estado</th>
              </tr>
            </thead>
            <tbody>
              {selected.sub_etapas.map((sub, i) => (
                <tr key={i} className="border-b border-slate-100 hover:bg-slate-50 transition">
                  <td className="py-3 px-4 font-semibold text-slate-800">{sub.nombre}</td>
                  <td className="py-3 px-4 text-right tabular-nums text-slate-700">
                    {sub.pacientes != null ? formatNum(escalarVolumen(sub.pacientes)) : "—"}
                  </td>
                  {selected.sub_etapas.some((s) => s.conversion != null) && (
                    <td className="py-3 px-4 text-right">
                      {sub.conversion != null ? (
                        <span className="text-xs font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full tabular-nums">
                          {sub.conversion}%
                        </span>
                      ) : "—"}
                    </td>
                  )}
                  <td className="py-3 px-4 text-xs text-slate-500">{sub.fuente}</td>
                  <td className="py-3 px-4 text-center">
                    <LeyendaEstado estado={sub.estado} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quiebres */}
        <SectionCard title="Quiebres críticos" subtitle={`Top problemas de ${selected.nombre}`} testid="quiebres-etapa">
          <div className="space-y-3">
            {selected.quiebres.map((q, i) => {
              const cfg = {
                alta: { bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-700", badge: "bg-rose-500" },
                media: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", badge: "bg-amber-500" },
                baja: { bg: "bg-slate-50", border: "border-slate-200", text: "text-slate-600", badge: "bg-slate-400" },
              }[q.severidad] || { bg: "bg-slate-50", border: "border-slate-200", text: "text-slate-600", badge: "bg-slate-400" };
              return (
                <div key={i} className={`rounded-xl border p-4 ${cfg.bg} ${cfg.border}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold text-white ${cfg.badge}`}>
                      {q.severidad.toUpperCase()}
                    </span>
                    {q.porcentaje != null && (
                      <span className={`text-lg font-bold tabular-nums ${cfg.text}`}>{q.porcentaje}%</span>
                    )}
                  </div>
                  <p className="text-sm text-slate-700">{q.descripcion}</p>
                  <p className="text-xs text-slate-500 mt-1">{q.motivo}</p>
                </div>
              );
            })}
          </div>
        </SectionCard>

        {/* Acciones */}
        <SectionCard title="Acciones recomendadas" subtitle={`Intervenciones para ${selected.nombre}`} testid="acciones-etapa">
          <div className="space-y-3">
            {selected.acciones.map((a, i) => (
              <div key={i} className="flex items-start gap-3 p-3 rounded-xl border border-slate-200 hover:bg-slate-50 transition">
                <div className="h-9 w-9 rounded-lg flex items-center justify-center text-white shrink-0"
                     style={{ backgroundColor: selected.color }}>
                  <ArrowRight className="w-4 h-4" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-slate-900">{a.titulo}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{a.descripcion}</div>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      </div>

      {/* Volumen por centro — datos reales proporcionales */}
      <SectionCard title="Volumen por centro" subtitle={`Distribución real (canales.dbo.reservas2) · ${filters.periodo}`} testid="top-centros">
        <div className="space-y-3">
          {centros.map((c) => {
            const maxPac = Math.max(...centros.map((x) => x.pacientes));
            return (
              <div key={c.nombre} className="p-3 rounded-xl border border-slate-200 hover:border-violet-300 hover:bg-violet-50/30 transition">
                <div className="flex justify-between items-center mb-2">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-3.5 h-3.5 text-slate-400" />
                    <span className="text-sm font-semibold text-slate-900">{c.nombre}</span>
                  </div>
                  <span className={`text-xs font-semibold tabular-nums ${c.fuga_pct > 18 ? "text-rose-600" : c.fuga_pct > 15 ? "text-amber-600" : "text-emerald-600"}`}>
                    {formatPct(c.fuga_pct)} no-show
                  </span>
                </div>
                <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full bg-violet-500 rounded-full" style={{ width: `${(c.pacientes / maxPac) * 100}%` }} />
                </div>
                <div className="text-xs text-slate-500 mt-1 tabular-nums">{formatNum(c.pacientes)} pacientes ({c.pct}%)</div>
              </div>
            );
          })}
        </div>
      </SectionCard>

      {/* Heatmap */}
      <SectionCard title="Heatmap de transiciones" subtitle="Tasa de conversión entre macro-etapas (%)" testid="heatmap-transitions">
        <HeatmapChart categories={heatmapLabels} series={heatmapSeries} height={360} />
      </SectionCard>

      {/* Previsión — datos reales proporcionales */}
      <SectionCard title="Desglose por previsión" subtitle={`Distribución real (adm_convenios) · ${filters.periodo}`} testid="prevision-breakdown">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {previsionData.map((x) => (
            <div key={x.p} className="rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: x.color }} />
                <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">{x.p}</span>
              </div>
              <div className="text-xl font-bold text-slate-900 mt-2 tabular-nums">{formatNum(x.pacientes)}</div>
              <div className="text-xs text-slate-500 tabular-nums">{x.pct}%</div>
            </div>
          ))}
        </div>
      </SectionCard>
    </div>
  );
}
